/**
 * ESS Logo Component
 *
 * A flexible logo component that supports:
 * - Image URLs (PNG, JPG, SVG files)
 * - Inline SVG with customizable colors
 * - Responsive height configuration
 *
 * @module customizations/components/theme/Logo
 * @see https://gitlab.earth.bsc.es/ess/essvoltocomponents/-/issues/25
 */
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import config from '@plone/volto/registry';
import { UniversalLink } from '@plone/volto/components';
import { toBackendLang, flattenToAppURL } from '@plone/volto/helpers';
import BscLogo from '../../../../images/BSC-white-large.png';
import './Logo.scss';

const messages = defineMessages({
  site: {
    id: 'Site',
    defaultMessage: 'Site',
  },
  homepage: {
    id: 'Back to homepage',
    defaultMessage: 'Back to homepage',
  },
});

/**
 * Logo Component
 *
 * @param {Object} props - Component props
 * @param {string} [props.src] - Image source URL (for image variant)
 * @param {React.ReactNode} [props.svg] - Inline SVG element (for SVG variant with color control)
 * @param {string} [props.svgString] - SVG as string (alternative to svg prop)
 * @param {string} [props.mobileHeight='45px'] - Logo height on mobile (<768px)
 * @param {string} [props.desktopHeight='55px'] - Logo height on desktop (>=768px)
 * @param {string} [props.alt] - Alt text for the logo
 * @param {string} [props.title] - Title attribute
 * @param {string} [props.href] - Link destination (default: home)
 * @param {string} [props.className='ess-logo'] - CSS class name
 * @param {string} [props.color] - Fill color for SVG variant
 * @param {string} [props.hoverColor] - Fill color on hover for SVG variant
 */
const Logo = ({
  src,
  svg,
  svgString,
  mobileHeight,
  desktopHeight,
  alt,
  title,
  href,
  className = 'ess-logo',
  color,
  hoverColor,
  ...rest
}) => {
  const { settings } = config;
  const lang = useSelector((state) => state.intl.locale);
  const intl = useIntl();
  const site = useSelector((state) => state.site.data);

  // Get heights from props or config
  const logoMobileHeight = mobileHeight || settings.logoMobileHeight || '45px';
  const logoDesktopHeight = desktopHeight || settings.logoDesktopHeight || '55px';

  // Resolve href: custom > multilingual home > home
  const resolvedHref = href || (settings.isMultilingual ? `/${toBackendLang(lang)}` : '/');

  // Resolve alt and title
  const resolvedAlt = alt || intl.formatMessage(messages.homepage);
  const resolvedTitle = title || intl.formatMessage(messages.site);

  // CSS custom properties for responsive sizing and colors
  const logoStyles = {
    '--logo-mobile-height': logoMobileHeight,
    '--logo-desktop-height': logoDesktopHeight,
    '--logo-color': color || 'currentColor',
    '--logo-hover-color': hoverColor || color || 'currentColor',
  };

  // Determine which variant to render
  const renderLogoContent = () => {
    // Variant 1: Inline SVG element (React component)
    if (svg) {
      return (
        <span
          className="ess-logo__svg-wrapper"
          style={logoStyles}
          role="img"
          aria-label={resolvedAlt}
        >
          {React.cloneElement(svg, {
            className: `ess-logo__svg ${svg.props?.className || ''}`.trim(),
            'aria-hidden': 'true',
          })}
        </span>
      );
    }

    // Variant 2: SVG as string (dangerouslySetInnerHTML)
    if (svgString) {
      return (
        <span
          className="ess-logo__svg-wrapper"
          style={logoStyles}
          role="img"
          aria-label={resolvedAlt}
          dangerouslySetInnerHTML={{ __html: svgString }}
        />
      );
    }

    // Variant 3: Image URL (default)
    const resolvedSrc = src
      || (site?.['plone.site_logo'] ? flattenToAppURL(site['plone.site_logo']) : BscLogo);

    return (
      <img
        src={resolvedSrc}
        alt={resolvedAlt}
        title={resolvedAlt}
        className="ess-logo__image"
        style={logoStyles}
      />
    );
  };

  return (
    <UniversalLink
      href={resolvedHref}
      title={resolvedTitle}
      className={className}
      {...rest}
    >
      {renderLogoContent()}
    </UniversalLink>
  );
};

export default Logo;
